import { ClipboardList, FileText, ListChecks, Receipt, ScrollText, Shield } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import type { OpenItems } from "@/hooks/use-project-attention";

interface OpenItemsStripProps {
  data: OpenItems | undefined;
  isLoading: boolean;
}

interface Tile {
  key: string;
  label: string;
  Icon: typeof FileText;
  count: number;
  overdue: number;
  dueSoon: number;
}

export function OpenItemsStrip({ data, isLoading }: OpenItemsStripProps) {
  if (isLoading || !data) {
    return (
      <div className="rounded-xl border border-border bg-card shadow-sm px-6 py-4">
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
          {["a", "b", "c", "d", "e", "f"].map((k) => (
            <Skeleton key={k} className="h-16 rounded-md" />
          ))}
        </div>
      </div>
    );
  }

  const tiles: Tile[] = [
    {
      key: "rfis",
      label: "RFIs",
      Icon: FileText,
      count: data.rfis.count,
      overdue: data.rfis.overdue,
      dueSoon: data.rfis.dueSoon,
    },
    {
      key: "submittals",
      label: "Submittals",
      Icon: ClipboardList,
      count: data.submittals.count,
      overdue: data.submittals.overdue,
      dueSoon: data.submittals.dueSoon,
    },
    {
      key: "payApps",
      label: "Pay Apps",
      Icon: Receipt,
      count: data.payApps.count,
      overdue: data.payApps.overdue,
      dueSoon: data.payApps.dueSoon,
    },
    {
      key: "changeOrders",
      label: "Change Orders",
      Icon: ScrollText,
      count: data.changeOrders.count,
      overdue: data.changeOrders.overdue,
      dueSoon: data.changeOrders.dueSoon,
    },
    {
      key: "punch",
      label: "Punch List",
      Icon: ListChecks,
      count: data.punch.count,
      overdue: data.punch.overdue,
      dueSoon: data.punch.dueSoon,
    },
    {
      key: "compliance",
      label: "Compliance",
      Icon: Shield,
      count: data.compliance.count,
      overdue: data.compliance.overdue,
      dueSoon: data.compliance.dueSoon,
    },
  ];

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm px-6 py-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[13px] font-semibold">Open items</h3>
      </div>
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
        {tiles.map((t) => {
          const { Icon } = t;
          return (
            <div
              key={t.key}
              className="text-left rounded-md border border-border hover:border-primary/30 hover:bg-secondary/40 transition-colors p-2.5"
            >
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Icon className="h-3.5 w-3.5" />
                <span className="text-[11px] font-medium truncate">{t.label}</span>
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <div className="text-[20px] font-semibold tabular-nums leading-none">{t.count}</div>
                {t.overdue > 0 ? (
                  <span className="text-[10.5px] font-semibold text-rose-600">
                    {t.overdue} late
                  </span>
                ) : t.dueSoon > 0 ? (
                  <span className="text-[10.5px] font-semibold text-amber-700">
                    {t.dueSoon} soon
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
